import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ExternalLink, Star, Award, TrendingUp, ThumbsUp, LayoutGrid, Globe, ArrowRight, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Container } from '../components/styled/Container';
import { PageLayout } from '../components/styled/PageLayout';
import { fetchSiteAndSimilarForSimilarPage, fetchSimilarSites, submitSimilarityVote } from '../store/actions/siteActions';
import { useLocale } from '../context/LocaleContext';
import SeoHead from '../components/SeoHead';
import { useT } from '../hooks/useT';
import { sitePathSlug } from '../utils/sitePathSlug';
import { getCategoryLabel } from '../utils/categoryDisplay';
import styled from 'styled-components';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  overflow: hidden;
`;

const AccentBar = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.primary};
`;

const CardInner = styled.div`
  padding: 2rem;
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const LogoLarge = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SiteTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const ScoreBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  color: ${({ theme }) => theme.colors.gold};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const DomainLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  margin-top: 0.25rem;
  &:hover {
    text-decoration: underline;
  }
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.75rem 0 0 0;
  max-width: 42rem;
  line-height: 1.6;
`;

const ViewDetailsBtn = styled(Link)`
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatBox = styled.div`
  padding: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ $bg }) => $bg};
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: ${({ $color }) => $color};
`;

const StatValue = styled.p`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  padding: 0.375rem 1rem;
  background: rgba(220, 38, 38, 0.1);
  color: ${({ theme }) => theme.colors.crimson};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const SimilarTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 3rem 0 1.5rem 0;
`;

/* One column: one card per row, only on this page */
const SimilarList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const SimilarLoadingMsg = styled.p`
  color: var(--muted-foreground);
  font-size: ${({ theme }) => theme.fontClamp.sm};
  margin: 0 0 1rem 0;
`;

const SimilarEmpty = styled.div`
  padding: 2rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  line-height: 1.55;
  max-width: 36rem;
`;

/* Horizontal card: content in a row, only for similar-sites on this page */
const SimilarCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
  }
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  padding: 1.25rem;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${({ theme }) => theme.shadowCardHover};
  }
`;

const SimilarCardLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const SimilarCardAvatar = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.lg};
  flex-shrink: 0;
`;

const SimilarCardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const SimilarCardDesc = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.25rem 0 0 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SimilarCardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const SimilarCardTag = styled.span`
  padding: 0.2rem 0.5rem;
  background: rgba(128, 0, 0, 0.1);
  color: ${({ theme }) => theme.colors.maroon};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const SimilarCardRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1rem;
  @media (min-width: 768px) {
    margin-top: 0;
    align-items: flex-end;
    flex-shrink: 0;
  }
`;

const HowSimilarLabel = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const StarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StarBtn = styled.button`
  padding: 0.125rem;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.mutedForeground};
  transition: color 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.gold};
  }
  &.filled {
    color: ${({ theme }) => theme.colors.gold};
  }
`;

const RankBadge = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

const ViewSimilarLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  white-space: nowrap;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

/* Vote modal */
const ModalOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  max-width: 28rem;
  width: 100%;
  padding: 1.5rem;
  position: relative;
`;

const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontClamp.lg};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0 0 1rem 0;
  padding-right: 2rem;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.mutedForeground};
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    background: ${({ theme }) => theme.colors.muted};
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const ModalRatingLabel = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0 0 0.5rem 0;
`;

const ModalReasonLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 1rem 0 0.5rem 0;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 4rem;
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  border: 2px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-family: inherit;
  color: ${({ theme }) => theme.colors.foreground};
  background: ${({ theme }) => theme.colors.background};
  resize: vertical;
  margin-bottom: 0.5rem;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalCharCount = styled.span`
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.25rem;
`;

const ModalBtn = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  border: none;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ModalSubmitBtn = styled(ModalBtn)`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const ModalCancelBtn = styled(ModalBtn)`
  background: ${({ theme }) => theme.colors.muted};
  color: ${({ theme }) => theme.colors.foreground};
  &:hover {
    background: ${({ theme }) => theme.colors.mutedForeground};
    color: ${({ theme }) => theme.colors.background};
  }
`;

const ModalError = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.crimson};
  margin: 0.5rem 0 0 0;
`;

const NotFoundWrap = styled.div`
  text-align: center;
  padding: 5rem 0;
`;

const NotFoundIcon = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const SubmitCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  &:hover {
    opacity: 0.9;
  }
`;

const REASON_MAX = 500;

function SimilarPage() {
  const dispatch = useDispatch();
  const { prependPath } = useLocale();
  const t = useT();
  const { slug: mainSlug } = useParams();
  const { site, similarList, loading, error } = useSelector((state) => state.sites);
  const [voteModal, setVoteModal] = useState(null);
  const [voteReason, setVoteReason] = useState('');
  const [voteSubmitting, setVoteSubmitting] = useState(false);
  const [voteError, setVoteError] = useState('');

  useEffect(() => {
    if (!mainSlug) return;
    dispatch(fetchSiteAndSimilarForSimilarPage(mainSlug));
  }, [dispatch, mainSlug]);

  const openVoteModal = (similarSite, rating) => {
    setVoteModal({ site: similarSite, rating });
    setVoteReason('');
    setVoteError('');
  };

  const closeVoteModal = () => {
    if (!voteSubmitting) setVoteModal(null);
  };

  const handleSubmitVote = async () => {
    if (!voteModal) return;
    setVoteSubmitting(true);
    setVoteError('');
    const result = await dispatch(
      submitSimilarityVote(mainSlug, voteModal.site.domain, voteModal.rating, voteReason.trim() || undefined)
    );
    setVoteSubmitting(false);
    if (result?.success) {
      setVoteModal(null);
      setVoteReason('');
    } else {
      setVoteError(result?.message || 'Failed to submit vote');
    }
  };

  if (loading && !site) {
    return (
      <PageLayout>
        <SeoHead title={`${mainSlug} | Similar sites | CiteMatch`} />
        <Navbar />
        <Container style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
          <p style={{ color: 'var(--muted-foreground)' }}>Loading site…</p>
        </Container>
        <Footer />
      </PageLayout>
    );
  }

  if (!site) {
    return (
      <PageLayout>
        <SeoHead title={`Site not found | CiteMatch`} noindex />
        <Navbar />
        <Container style={{ paddingTop: '5rem', paddingBottom: '5rem' }}>
          <NotFoundWrap>
            <NotFoundIcon>
              <Globe size={32} />
            </NotFoundIcon>
            <h1 style={{ margin: 0, fontSize: '1.875rem' }}>Site not found</h1>
            <p style={{ color: 'var(--muted-foreground)', marginTop: '0.5rem' }}>
              {error ? error : `"${mainSlug}" is not in our database yet.`}
            </p>
            <SubmitCta to={prependPath('/submit')}>Submit this site <ArrowRight size={14} /></SubmitCta>
          </NotFoundWrap>
        </Container>
        <Footer />
      </PageLayout>
    );
  }

  const simSeo = site?.similarPageSeo || {};
  const d = site?.domain || mainSlug;
  const pageTitle = simSeo.title ? String(simSeo.title) : `Sites similar to ${d} | CiteMatch`;
  const pageDescription =
    (simSeo.description && String(simSeo.description)) ||
    `See alternative websites similar to ${d}. Rate how similar each alternative is and help improve the ranking.`;
  const simKeywords = simSeo.keywords ? String(simSeo.keywords) : undefined;
  const simNoindex = simSeo.robots && String(simSeo.robots).toLowerCase().includes('noindex');
  const simCanon = simSeo.canonicalPath;
  const similarCanonical =
    simCanon && String(simCanon).trim()
      ? prependPath(String(simCanon).trim().startsWith('/') ? String(simCanon).trim() : `/${String(simCanon).trim()}`)
      : undefined;

  return (
    <PageLayout>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        keywords={simKeywords}
        canonicalPath={similarCanonical}
        noindex={simNoindex}
      />
      <Navbar />
      <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={prependPath('/')}><ArrowLeft size={16} strokeWidth={2.5} /> {t('common.back')}</BackLink>

        <Card initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <AccentBar />
          <CardInner>
            <HeaderRow>
              <LogoLarge>{site.title.charAt(0)}</LogoLarge>
              <HeaderInfo>
                <TitleRow>
                  <SiteTitle>{site.title}</SiteTitle>
                  <ScoreBadge><Star size={12} fill="currentColor" /> {(site.similarityScore / 10).toFixed(1)}</ScoreBadge>
                </TitleRow>
                <DomainLink href={`https://${site.domain}`} target="_blank" rel="noopener noreferrer">
                  {site.domain} <ExternalLink size={12} />
                </DomainLink>
                <Description>{site.description}</Description>
              </HeaderInfo>
              <ViewDetailsBtn to={prependPath(`/site/${sitePathSlug(site)}`)}>
                View details <ArrowRight size={14} />
              </ViewDetailsBtn>
            </HeaderRow>

            <StatsGrid>
              {site.alternativeRank != null && site.alternativeTo && (
                <StatBox $bg="rgba(0,48,135,0.05)">
                  <StatLabel $color="var(--primary)"><Award size={14} strokeWidth={2.5} /> Alternative Rank</StatLabel>
                  <StatValue>#{site.alternativeRank} to {site.alternativeTo}</StatValue>
                </StatBox>
              )}
              {site.trending && (
                <StatBox $bg="rgba(34,197,94,0.08)">
                  <StatLabel $color="hsl(142,71%,45%)"><TrendingUp size={14} strokeWidth={2.5} /> Momentum</StatLabel>
                  <StatValue style={{ fontSize: 'inherit' }}>{site.trending}</StatValue>
                </StatBox>
              )}
              {site.userScore != null && (
                <StatBox $bg="rgba(234,179,8,0.05)">
                  <StatLabel $color="var(--gold)"><ThumbsUp size={14} strokeWidth={2.5} /> User Score</StatLabel>
                  <StatValue>{site.userScore}/5</StatValue>
                </StatBox>
              )}
              <StatBox $bg="rgba(128,0,0,0.05)">
                <StatLabel $color="var(--maroon)"><LayoutGrid size={14} strokeWidth={2.5} /> Category</StatLabel>
                <StatValue style={{ fontSize: '1.25rem', fontWeight: 600 }}>{site.categoryName || getCategoryLabel(site.category)}</StatValue>
              </StatBox>
            </StatsGrid>

            <TagsWrap>
              {site.tags.map((tag) => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagsWrap>
          </CardInner>
        </Card>

        <SimilarTitle>
          Sites Similar to <span style={{ color: 'var(--primary)' }}>{site.title}</span>
        </SimilarTitle>
        {loading && site ? (
          <SimilarLoadingMsg>Loading similar sites…</SimilarLoadingMsg>
        ) : null}
        {!loading && Array.isArray(similarList) && similarList.length === 0 ? (
          <SimilarEmpty>
            No similar sites have been published for this page yet. Alternatives are chosen in the admin under{' '}
            <strong>Similar sites (curation)</strong> — add a curated list for this base site to show entries here.
          </SimilarEmpty>
        ) : null}
        <SimilarList>
          {!loading && similarList.map((s, index) => (
            <SimilarCard
              key={s.id || s.domain}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <SimilarCardLeft style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <SimilarCardAvatar>{s.title ? s.title.charAt(0).toUpperCase() : s.domain.charAt(0)}</SimilarCardAvatar>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <SimilarCardTitle>{s.domain}</SimilarCardTitle>
                  <SimilarCardDesc>{s.description}</SimilarCardDesc>
                  {s.tags && s.tags.length > 0 && (
                    <SimilarCardTags>
                      {s.tags.slice(0, 4).map((tag) => (
                        <SimilarCardTag key={tag}>{tag}</SimilarCardTag>
                      ))}
                    </SimilarCardTags>
                  )}
                </div>
              </SimilarCardLeft>
              <SimilarCardRight>
                <HowSimilarLabel>How similar to {site.title}?</HowSimilarLabel>
                <StarRow>
                  {[1, 2, 3, 4, 5].map((n) => {
                    const avg = s.similarityVoteStats?.averageRating ?? 0;
                    const filled = n <= Math.round(avg);
                    return (
                      <StarBtn
                        key={n}
                        type="button"
                        className={filled ? 'filled' : ''}
                        onClick={() => openVoteModal(s, n)}
                        aria-label={`${n} star - rate similarity`}
                      >
                        <Star size={20} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.5} />
                      </StarBtn>
                    );
                  })}
                </StarRow>
                {s.similarityVoteStats && s.similarityVoteStats.voteCount > 0 && (
                  <RankBadge>
                    #{index + 1} to {site.title} · {s.similarityVoteStats.averageRating}/5 ({s.similarityVoteStats.voteCount} votes)
                  </RankBadge>
                )}
                <ViewSimilarLink to={prependPath(`/similar/${sitePathSlug(s)}`)}>
                  View similar <ArrowRight size={14} />
                </ViewSimilarLink>
              </SimilarCardRight>
            </SimilarCard>
          ))}
        </SimilarList>
      </Container>
      <Footer />

      <AnimatePresence>
        {voteModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeVoteModal}
          >
            <ModalCard
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <ModalClose type="button" onClick={closeVoteModal} aria-label="Close" disabled={voteSubmitting}>
                <X size={18} />
              </ModalClose>
              <ModalTitle>
                Why is {voteModal.site.domain} similar to {site.title}?
              </ModalTitle>
              <ModalRatingLabel>Your rating: {voteModal.rating}/5</ModalRatingLabel>
              <StarRow style={{ marginBottom: 0 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star size={22} key={n} fill={n <= voteModal.rating ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.5} style={{ color: 'var(--gold)' }} />
                ))}
              </StarRow>
              <ModalReasonLabel htmlFor="vote-reason">Your reason (optional)</ModalReasonLabel>
              <ModalTextarea
                id="vote-reason"
                value={voteReason}
                onChange={(e) => setVoteReason(e.target.value.slice(0, REASON_MAX))}
                placeholder="e.g. Both offer payment APIs and developer tools..."
                maxLength={REASON_MAX}
                rows={3}
                disabled={voteSubmitting}
              />
              <ModalCharCount>{voteReason.length}/{REASON_MAX}</ModalCharCount>
              {voteError && <ModalError>{voteError}</ModalError>}
              <ModalActions>
                <ModalSubmitBtn type="button" onClick={handleSubmitVote} disabled={voteSubmitting}>
                  {voteSubmitting ? 'Submitting…' : 'Submit vote'}
                </ModalSubmitBtn>
                <ModalCancelBtn type="button" onClick={closeVoteModal} disabled={voteSubmitting}>
                  Cancel
                </ModalCancelBtn>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </PageLayout>
  );
}

export default SimilarPage;
